import React, { useCallback } from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';

import { Autocomplete, MenuItem, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';

import { COUNTRIES_LIST } from '~/components/Common/CustomOffers/constants';
import DateWidget from '~/components/Common/Elements/DateWidget';

import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';

import UsersService from '~/services/UsersService';

type Props = {
  control: Control<CustomOfferFormValues>;
  employees: Array<App.BusinessEmployee>;
  errors: FieldErrors<CustomOfferFormValues>;
  includeMiddleName: boolean;
  index: number;
  isTravellerCountryRequired?: boolean;
  isSubmitting: boolean;
  register: UseFormRegister<CustomOfferFormValues>;
  setValue: UseFormSetValue<CustomOfferFormValues>;
};

export default function CustomOfferBusinessTravellerWidget({
  control,
  employees,
  errors,
  includeMiddleName,
  index,
  isTravellerCountryRequired = true,
  isSubmitting,
  register,
  setValue,
}: Props) {
  const handleEmployeeSelect = useCallback(
    async (_: React.SyntheticEvent, selectedEmployee: App.BusinessEmployee | null) => {
      if (selectedEmployee) {
        setValue(`travellers.${index}.first_name`, selectedEmployee.firstName);
        setValue(`travellers.${index}.last_name`, selectedEmployee.lastName);
        setValue(`travellers.${index}.email`, selectedEmployee.email);

        const userRecord = (await UsersService.getUser(selectedEmployee.customerId, {
          brand: LE_BUSINESS_TRAVELLER,
        })) as App.User;

        if (userRecord.dob) {
          setValue(`travellers.${index}.date_of_birth`, userRecord.dob);
        }
      }
    },
    [index, setValue],
  );

  const getOptionLabel = useCallback((option?: App.BusinessEmployee) => {
    if (!option) {
      return '<Guest traveller>';
    }

    return `${option.firstName} ${option.lastName}`;
  }, []);

  return (
    <>
      <Grid size={{ xs: 6, md: 1 }}>
        <Controller
          name={`travellers.${index}.employee`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              options={[null, ...employees]}
              getOptionLabel={getOptionLabel}
              onChange={async (event, selected) => {
                field.onChange(selected);
                await handleEmployeeSelect(event, selected);
              }}
              disabled={isSubmitting}
              renderInput={(params) => (
                <TextField
                  {...params}
                  helperText={error?.message || errors.travellers?.[index]?.employee?.message}
                  error={!!error && !!errors.travellers?.[index]?.employee}
                  label="Employee"
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 1 }}>
        <TextField
          {...register(`travellers.${index}.first_name`)}
          helperText={errors.travellers?.[index]?.first_name?.message || errors.travellers?.[index]?.name?.message}
          error={!!errors.travellers?.[index]?.first_name && !!errors.travellers?.[index]?.name}
          disabled={isSubmitting}
          label="First name"
          required
          fullWidth
          data-testid={`traveller-first-name-${index}`}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Grid>
      {includeMiddleName && (
        <Grid size={{ xs: 6, md: 1 }}>
          <TextField
            {...register(`travellers.${index}.middle_name`)}
            helperText={errors.travellers?.[index]?.middle_name?.message}
            error={!!errors.travellers?.[index]?.middle_name}
            disabled={isSubmitting}
            label="Middle name"
            fullWidth
            data-testid={`traveller-middle_name-${index}`}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>
      )}
      <Grid size={{ xs: 6, md: 1 }}>
        <TextField
          {...register(`travellers.${index}.last_name`)}
          helperText={errors.travellers?.[index]?.last_name?.message}
          error={!!errors.travellers?.[index]?.last_name}
          disabled={isSubmitting}
          label="Last name"
          required
          fullWidth
          data-testid={`traveller-last-name-${index}`}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <TextField
          {...register(`travellers.${index}.email`)}
          helperText={errors.travellers?.[index]?.email?.message}
          error={!!errors.travellers?.[index]?.email}
          disabled={isSubmitting}
          label="Email"
          type="email"
          fullWidth
          data-testid={`traveller-email-${index}`}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 1 }}>
        <Controller
          name={`travellers.${index}.country`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Country of residence"
              required={isTravellerCountryRequired}
              helperText={error?.message}
              error={!!error}
              disabled={isSubmitting}
              select
              fullWidth
              data-testid={`traveller-country-${index}`}
            >
              {COUNTRIES_LIST.map((country) => (
                <MenuItem value={country} key={country}>
                  {country}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 1 }}>
        <Controller
          control={control}
          name={`travellers.${index}.date_of_birth`}
          render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
            <DateWidget
              {...field}
              slotProps={{ textField: { helperText: error?.message, error: !!error } }}
              disabled={isSubmitting}
              label="Date of birth"
              fullWidth
              data-testid={`traveller-date-of-birth-${index}`}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 6, md: 1 }}>
        <Controller
          name={`travellers.${index}.type`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Traveller type"
              required
              helperText={error?.message}
              error={!!error}
              disabled={isSubmitting}
              select
              fullWidth
              data-testid={`traveller-type-${index}`}
            >
              <MenuItem value="adult">Adult</MenuItem>
              <MenuItem value="child">Child</MenuItem>
              <MenuItem value="infant">Infant</MenuItem>
            </TextField>
          )}
        />
      </Grid>
    </>
  );
}
